import { MapPin, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import StoreMap from "./StoreMap";
import { normalizeStoreNumber } from "../utils/userUtils";
import { useStores } from "../hooks/useStores";
import { useTheme } from "../context/ThemeContext";
import { useUserContext } from "../context/UserContext";
import { geocodeZipCode } from "../utils/geocoding";
import {
  findDollarGeneralStoreInList,
  getDollarGeneralGeo,
  type WalmartStore,
  type HomeDepotStore,
  type DollarGeneralStore,
} from "../utils/storeUtils";
import type { RetailerType } from "../config/retailers";

interface StoreLocationCardProps {
  storeId: string;
  retailer?: RetailerType;
}

export default function StoreLocationCard({
  storeId,
  retailer = "walmart",
}: StoreLocationCardProps) {
  const { isDark } = useTheme();
  const { user } = useUserContext();
  const [userCoords, setUserCoords] = useState<{ lat: number; long: number } | null>(null);

  const { stores: loadedStores } = useStores<
    WalmartStore[] | HomeDepotStore[] | DollarGeneralStore[]
  >(retailer);

  useEffect(() => {
    const zip = user?.zipCode?.trim();
    if (!zip || !/^\d{5}$/.test(zip)) {
      setUserCoords(null);
      return;
    }
    geocodeZipCode(zip)
      .then((coords) => setUserCoords(coords))
      .catch(() => setUserCoords(null));
  }, [user?.zipCode]);

  if (!loadedStores) {
    return null;
  }

  let currentStore: any = null;

  if (retailer === "home_depot") {
    const normalizedStoreId = normalizeStoreNumber(storeId, retailer);
    const paddedStoreId = normalizedStoreId.padStart(4, "0");
    currentStore = (loadedStores as HomeDepotStore[]).find(
      (store: any) => store.store_number === paddedStoreId
    );
  } else if (retailer === "dollar_general") {
    currentStore = findDollarGeneralStoreInList(
      loadedStores as DollarGeneralStore[],
      storeId,
      userCoords ?? undefined
    );
  } else {
    currentStore = (loadedStores as WalmartStore[]).find(
      (store: any) => store.store_id === storeId
    );
  }

  const geoData =
    retailer === "home_depot"
      ? {
          lat: parseFloat(currentStore?.latitude),
          long: parseFloat(currentStore?.longitude),
        }
      : retailer === "dollar_general"
      ? getDollarGeneralGeo(currentStore)
      : currentStore?.geo;

  if (!currentStore || !geoData || !geoData.lat || !geoData.long) {
    return (
      <div className="bg-white dark:bg-dark-900 border border-primary-200/50 dark:border-dark-700 rounded-xl mb-4 p-6 text-center">
        <div className="max-w-xs mx-auto">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/40 dark:to-primary-800/40 rounded-full flex items-center justify-center mx-auto mb-3">
            <MapPin className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Store Location Unavailable
          </h3>
          <p className="text-sm text-gray-600 dark:text-dark-300">
            Location data for store #{storeId} is not available.
          </p>
        </div>
      </div>
    );
  }

  // Get store information based on retailer - format for StoreMap component
  const storeInfo =
    retailer === "home_depot"
      ? {
          store_id: storeId,
          postal_code: currentStore.zip,
          address: `${currentStore.address_1}, ${currentStore.city}, ${currentStore.state} ${currentStore.zip}`,
          geo: {
            lat: geoData.lat,
            long: geoData.long,
          },
        }
      : {
          store_id: storeId,
          postal_code: currentStore.postal_code,
          address: currentStore.address,
          geo: {
            lat: geoData.lat,
            long: geoData.long,
          },
        };

  const darkTileUrl = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

  return (
    <div className="bg-white dark:bg-dark-900 border border-primary-200/50 dark:border-dark-700 rounded-xl mb-4 overflow-hidden">
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center">
            <MapPin className="h-5 w-5 mr-2" />
            Store Location
          </h2>
          <div className="flex items-center space-x-2">
            <span className="text-primary-100 text-sm font-medium">
              #{storeId}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-4">
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-700 dark:text-dark-200 flex items-center">
              <MapPin className="h-4 w-4 text-primary-600 dark:text-primary-400 mr-2 flex-shrink-0" />
              Address:
            </div>
            <div className="text-sm text-gray-600 dark:text-dark-300 ml-6">
              {storeInfo.address}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <StoreMap
            stores={[storeInfo]}
            selectedStoreId={storeId}
            center={[geoData.lat, geoData.long]}
            height="280px"
            zoom={14}
            className="rounded-lg overflow-hidden border border-primary-200/50 dark:border-dark-700"
            tileUrl={isDark ? darkTileUrl : undefined}
          />
        </div>

        <div className="flex space-x-3">
          <a
            href={
              retailer === "home_depot"
                ? `https://www.homedepot.com/l/${currentStore.store_number}`
                : retailer === "dollar_general"
                ? `https://www.dollargeneral.com/store/${storeId}`
                : `https://www.walmart.com/store/${storeId}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 font-medium text-sm transform hover:scale-105"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Store Details
          </a>
          <a
            href={`https://maps.google.com/maps?q=${geoData.lat},${geoData.long}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center px-4 py-2.5 bg-white dark:bg-dark-800 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-50 dark:hover:bg-dark-700 transition-all duration-200 font-medium text-sm border-2 border-primary-200 dark:border-dark-600 hover:border-primary-300 dark:hover:border-dark-500 transform hover:scale-105"
          >
            <MapPin className="h-4 w-4 mr-2" />
            Directions
          </a>
        </div>
      </div>
    </div>
  );
}
